import { useMutation, useQuery } from "@tanstack/react-query";
import { useState } from "react";

import { ApiError, api } from "../../api/client";
import { HelpIcon } from "../../shell/HelpIcon";
import { CasesApi, type Case } from "../cases/api";
import { IntelApi } from "../intel/api";
import { POIApi } from "../poi/api";
import { TasksApi } from "../tasks/api";
import { PerformanceApi } from "../performance/api";
import { PopularityApi } from "../popularity/api";
import { ReportsApi, type CaseSummary, type CaseTrendPoint } from "./api";

type TabKey = "summary" | "raw";

export function Reports() {
  const [tab, setTab] = useState<TabKey>("summary");

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", marginBottom: 4 }}>
        <h1 style={{ margin: 0, fontSize: 22, fontWeight: 600 }}>Reports</h1>
        <ExportButton />
      </div>
      <p className="muted" style={{ margin: "0 0 20px", fontSize: 14 }}>
        Aggregates over your visible case data. Numbers respect your scope and classification level.
      </p>

      <Tabs value={tab} onChange={setTab} />

      <div style={{ marginTop: 20 }}>
        {tab === "summary" ? <SummaryTab /> : <RawDataTab />}
      </div>
    </div>
  );
}

function Tabs({ value, onChange }: { value: TabKey; onChange: (v: TabKey) => void }) {
  return (
    <div role="tablist" style={{ display: "flex", gap: 4, borderBottom: "1px solid var(--border)" }}>
      <TabButton active={value === "summary"} onClick={() => onChange("summary")}>Summary</TabButton>
      <TabButton active={value === "raw"} onClick={() => onChange("raw")}>Raw data</TabButton>
    </div>
  );
}

function TabButton({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      role="tab"
      aria-selected={active}
      onClick={onClick}
      className="ghost"
      style={{
        background: "transparent",
        color: active ? "var(--accent)" : "var(--muted)",
        border: "none",
        borderBottom: `2px solid ${active ? "var(--accent)" : "transparent"}`,
        borderRadius: 0,
        padding: "10px 16px",
        marginBottom: -1,
        fontWeight: 600,
      }}
    >
      {children}
    </button>
  );
}

function ExportButton() {
  const [error, setError] = useState<string | null>(null);
  const exportCsv = useMutation({
    mutationFn: ReportsApi.exportCasesCsv,
    onSuccess: ({ filename, blob }) => {
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = filename; document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    },
    onError: (e) => setError(e instanceof ApiError ? e.message : (e as Error).message),
  });
  return (
    <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
      {error && <span className="muted" style={{ fontSize: 12, color: "var(--danger)" }}>{error}</span>}
      <button
        onClick={() => {
          if (window.confirm("Download cases as CSV? The export includes the records you can already see and is audit-logged. Don't share the file outside the team."))
            exportCsv.mutate();
        }}
        disabled={exportCsv.isPending}
      >
        {exportCsv.isPending ? "Exporting…" : "Export CSV"}
      </button>
    </div>
  );
}

// --- SUMMARY TAB ----------------------------------------------------------

function SummaryTab() {
  const summary = useQuery({ queryKey: ["report.caseSummary"], queryFn: ReportsApi.caseSummary });
  const trend = useQuery({ queryKey: ["report.caseTrend"], queryFn: ReportsApi.caseTrend });

  if (summary.isLoading) return <LoadingPlaceholder />;
  if (summary.error) return <ErrorBox label="Case summary" err={summary.error} />;
  if (!summary.data) return <div className="empty">No data yet. Create some cases to populate this dashboard.</div>;

  const s: CaseSummary = summary.data;

  return (
    <div>
      <SummaryCallouts />

      <h2 style={{ fontSize: 14, fontWeight: 600, color: "var(--muted)", textTransform: "uppercase", letterSpacing: 0.6, margin: "32px 0 12px" }}>
        Cases at a glance
        <HelpIcon title="Cases at a glance">
          Counts of every investigation case visible to you, broken out by status and classification.
          Numbers respect your scope (you only see cases in your assigned area) and clearance.
        </HelpIcon>
      </h2>

      <div className="stat-row">
        <Stat label="Total cases" value={s.total} />
        <Stat label="Open" value={s.byStatus.open ?? 0} accent="warning" />
        <Stat label="In review" value={s.byStatus.in_review ?? 0} />
        <Stat label="Closed" value={s.byStatus.closed ?? 0} accent="success" />
      </div>

      <div className="report-grid">
        <Breakdown
          title="Cases by classification"
          help="How sensitive your visible cases are. Restricted/Secret tend to indicate ongoing operations; Unclassified is routine."
          data={s.byClassification}
        />
        <Breakdown
          title="Cases by branch"
          help="Which branches are generating the most case activity. A branch with very few cases can mean low activity OR low capture discipline."
          data={s.byBranch}
        />
      </div>

      {trend.data && trend.data.series.length > 0 && (
        <div className="card" style={{ marginTop: 24 }}>
          <h3 className="card-title">
            Cases opened per day
            <HelpIcon title="Cases opened per day">
              Each dot is the number of new investigation cases opened on that day in your visible scope.
              A spike usually correlates with an incident or surveillance push; a flat zero may indicate a quiet
              period — or that field officers aren't logging.
            </HelpIcon>
          </h3>
          <p className="card-sub">Last {trend.data.series.length} days with activity.</p>
          <LineChart points={trend.data.series} yLabel="New cases" />
        </div>
      )}

      <CrossModuleSummaries />
    </div>
  );
}

// Convert anything with a `createdAt` (or `publishedAt`) ISO string into a
// per-day point series ready for the LineChart.
function buildTrendSeries(items: { createdAt?: string; publishedAt?: string }[] | undefined, days = 14): CaseTrendPoint[] {
  if (!items?.length) return [];
  const now = new Date();
  const buckets: Record<string, number> = {};
  for (let i = 0; i < days; i++) {
    const d = new Date(now);
    d.setUTCDate(d.getUTCDate() - i);
    buckets[d.toISOString().slice(0, 10)] = 0;
  }
  for (const it of items) {
    const ts = it.createdAt ?? it.publishedAt;
    if (!ts) continue;
    const day = ts.slice(0, 10);
    if (day in buckets) buckets[day] += 1;
  }
  return Object.entries(buckets)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([date, count]) => ({ date, count }));
}

// Top-of-page plain-English callouts — converts the raw counts into headline
// sentences a duty officer can scan in one glance.
function SummaryCallouts() {
  const intel = useQuery({ queryKey: ["intel"], queryFn: () => IntelApi.list() });
  const cases = useQuery({ queryKey: ["report.rawCases"], queryFn: () => CasesApi.list() });
  const poi = useQuery({ queryKey: ["poi"], queryFn: () => POIApi.list() });
  const tasks = useQuery({ queryKey: ["tasks"], queryFn: () => TasksApi.list() });

  const criticalIntel = (intel.data?.items ?? []).filter((i) => i.severity === "critical" && !i.redactedAt).length;
  const highIntel = (intel.data?.items ?? []).filter((i) => i.severity === "high" && !i.redactedAt).length;
  const openCases = (cases.data?.items ?? []).filter((c) => c.status === "open").length;
  const reviewCases = (cases.data?.items ?? []).filter((c) => c.status === "in_review").length;
  const highThreatPOI = (poi.data?.items ?? []).filter((p) => p.threatLevel === "high" && p.status === "active").length;
  const overdueTasks = (tasks.data?.items ?? []).filter((t) => {
    if (t.status === "done" || t.status === "cancelled") return false;
    if (!t.dueAt) return false;
    return new Date(t.dueAt).getTime() < Date.now();
  }).length;

  const callouts: { tone: "danger" | "warning" | "info"; text: string }[] = [];
  if (criticalIntel > 0) callouts.push({ tone: "danger", text: `${criticalIntel} critical intel item${criticalIntel === 1 ? "" : "s"} unredacted — urgent attention.` });
  if (highIntel > 0) callouts.push({ tone: "warning", text: `${highIntel} high-severity intel item${highIntel === 1 ? "" : "s"} active.` });
  if (highThreatPOI > 0) callouts.push({ tone: "warning", text: `${highThreatPOI} high-threat individual${highThreatPOI === 1 ? "" : "s"} on the active watchlist.` });
  if (overdueTasks > 0) callouts.push({ tone: "warning", text: `${overdueTasks} task${overdueTasks === 1 ? "" : "s"} overdue.` });
  if (openCases > 0) callouts.push({ tone: "info", text: `${openCases} case${openCases === 1 ? "" : "s"} open${reviewCases ? `, ${reviewCases} in review` : ""}.` });
  if (callouts.length === 0) {
    callouts.push({ tone: "info", text: "Nothing pressing in your scope. All quiet — for now." });
  }

  return (
    <div>
      <h2 style={{ fontSize: 14, fontWeight: 600, color: "var(--muted)", textTransform: "uppercase", letterSpacing: 0.6, margin: "0 0 12px" }}>
        At-a-glance
        <HelpIcon title="At-a-glance">
          Plain-English callouts summarising the most important counts in your scope right now.
          Built from live data — not cached. Top callouts are coloured red (urgent), amber (attention), grey (informational).
        </HelpIcon>
      </h2>
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {callouts.map((c, i) => (
          <div
            key={i}
            className="card"
            style={{
              padding: "10px 14px",
              borderLeft: `4px solid ${c.tone === "danger" ? "var(--danger)" : c.tone === "warning" ? "var(--warning)" : "var(--accent)"}`,
              fontSize: 14,
            }}
          >
            {c.text}
          </div>
        ))}
      </div>
    </div>
  );
}

function CrossModuleSummaries() {
  const intel = useQuery({ queryKey: ["intel"], queryFn: () => IntelApi.list() });
  const poi = useQuery({ queryKey: ["poi"], queryFn: () => POIApi.list() });
  const tasks = useQuery({ queryKey: ["tasks"], queryFn: () => TasksApi.list() });
  const perf = useQuery({ queryKey: ["performance"], queryFn: () => PerformanceApi.list() });
  const pop = useQuery({ queryKey: ["popularity"], queryFn: () => PopularityApi.list() });

  function bucket<T>(items: T[] | undefined, key: (i: T) => string | undefined): Record<string, number> {
    const m: Record<string, number> = {};
    for (const i of items ?? []) {
      const k = key(i) ?? "unknown";
      m[k] = (m[k] ?? 0) + 1;
    }
    return m;
  }

  const intelBySev = bucket(intel.data?.items, (i) => i.severity);
  const poiByThreat = bucket(poi.data?.items, (p) => p.threatLevel);
  const tasksByStatus = bucket(tasks.data?.items, (t) => t.status);
  const tasksByPriority = bucket(tasks.data?.items, (t) => t.priority);
  const perfByRating = bucket(perf.data?.items, (r) => r.rating);
  const popByScore = bucket(pop.data?.items, (r) => r.score);

  // Cases too — for the bar chart by classification below.
  const cases = useQuery({ queryKey: ["report.rawCases"], queryFn: () => CasesApi.list() });

  return (
    <div style={{ marginTop: 32 }}>
      <h2 style={{ fontSize: 14, fontWeight: 600, color: "var(--muted)", textTransform: "uppercase", letterSpacing: 0.6, margin: "0 0 12px" }}>
        Across the platform
        <HelpIcon title="Across the platform">
          Counts and shapes for the rest of the operational data, not just cases.
          Each block below is a single category — tap the ⓘ on each to learn what its breakdown means.
        </HelpIcon>
      </h2>
      <div className="report-grid">
        <Breakdown
          title="Intel by severity"
          help="Captured intel items grouped by how urgent they are. Critical/High items push notifications immediately to the chain of command."
          data={intelBySev}
        />
        <Breakdown
          title="POI by threat level"
          help="People on the watchlist by how dangerous they're judged to be. High-threat additions push notifications upward."
          data={poiByThreat}
        />
      </div>
      <div className="report-grid" style={{ marginTop: 16 }}>
        <Breakdown
          title="Tasks by status"
          help="What stage your tasks are at. Open and In Progress are the action queue; Done/Cancelled are history."
          data={tasksByStatus}
        />
        <Breakdown
          title="Tasks by priority"
          help="Urgent and High priority tasks push notifications immediately. A pile of Urgents that aren't moving is a flag."
          data={tasksByPriority}
        />
      </div>
      <div className="report-grid" style={{ marginTop: 16 }}>
        <Breakdown
          title="Performance by rating"
          help="How team members are being rated. A bias toward Failing/Poor warrants intervention; mostly Excellent may indicate inflated reporting."
          data={perfByRating}
        />
        <Breakdown
          title="Popularity by score"
          help="Sentiment readings about the people you're tracking. Very High = strong support; Very Low = active hostility on the ground."
          data={popByScore}
        />
      </div>

      <h2 style={{ fontSize: 14, fontWeight: 600, color: "var(--muted)", textTransform: "uppercase", letterSpacing: 0.6, margin: "32px 0 12px" }}>
        Distribution by severity / priority
        <HelpIcon title="Distribution by severity / priority">
          One bar per bucket so you can see at-a-glance how operational data is distributed.
          Use these to spot risk concentration — a bar chart heavy on "Critical" intel or "Failing"
          performance is a red flag.
        </HelpIcon>
      </h2>
      <div className="report-grid">
        <BarChart
          title="Intel by severity"
          buckets={SEV_ORDER.intel}
          data={intelBySev}
          help="Stacking up at Critical/High means more attention is needed; mostly Low usually means quiet."
        />
        <BarChart
          title="POI by threat level"
          buckets={SEV_ORDER.poi}
          data={poiByThreat}
          help="A surge of High-threat additions warrants a constituency-level briefing."
        />
      </div>
      <div className="report-grid" style={{ marginTop: 16 }}>
        <BarChart
          title="Tasks by priority"
          buckets={SEV_ORDER.tasks}
          data={tasksByPriority}
          help="Lots of Urgent tasks in flight is fine if they're moving; pile-ups are a warning."
        />
        <BarChart
          title="Cases by classification"
          buckets={SEV_ORDER.cases}
          data={bucket(cases.data?.items, (c) => c.classification)}
          help="A high share of Restricted/Secret cases means more sensitivity in your scope; plan briefings accordingly."
        />
      </div>
      <div className="report-grid" style={{ marginTop: 16 }}>
        <BarChart
          title="Performance by rating"
          buckets={SEV_ORDER.performance}
          data={perfByRating}
          help="If Failing/Poor dominate, intervene early; if everything is Excellent the inflation is a problem too."
        />
        <BarChart
          title="Popularity by score"
          buckets={SEV_ORDER.popularity}
          data={popByScore}
          help="A skew toward Very Low in any area = active hostility; a spike at Very High is unusually positive — verify."
        />
      </div>
    </div>
  );
}

// Stable left-to-right ordering of the buckets per data source. Matches the
// natural severity / priority gradient.
const SEV_ORDER = {
  intel: ["critical", "high", "medium", "low"],
  poi: ["high", "medium", "low"],
  tasks: ["urgent", "high", "medium", "low"],
  cases: ["secret", "restricted", "unclassified"],
  performance: ["failing", "poor", "satisfactory", "good", "excellent"],
  popularity: ["very_low", "low", "medium", "high", "very_high"],
};

const BUCKET_BAR_COLORS: Record<string, string> = {
  critical: "#b91c1c", high: "#dc2626", urgent: "#dc2626",
  medium: "#d97706",
  low: "#16a34a",
  secret: "#7c3aed", restricted: "#dc2626", unclassified: "#16a34a",
  failing: "#b91c1c", poor: "#dc2626", satisfactory: "#d97706",
  good: "#16a34a", excellent: "#15803d",
  very_low: "#b91c1c", very_high: "#15803d",
  unknown: "#64748b",
};

function BarChart({ title, buckets, data, help }: {
  title: string;
  buckets: string[];
  data: Record<string, number>;
  help: string;
}) {
  const W = 360;
  const H = 200;
  const PADDING = { top: 16, right: 12, bottom: 36, left: 32 };
  const innerW = W - PADDING.left - PADDING.right;
  const innerH = H - PADDING.top - PADDING.bottom;

  const values = buckets.map((b) => data[b] ?? 0);
  const max = Math.max(...values, 1);
  const yTicks = niceTicks(max, 4);
  const niceMax = yTicks[yTicks.length - 1];

  const barW = innerW / buckets.length * 0.7;
  const stepW = innerW / buckets.length;
  const total = values.reduce((s, n) => s + n, 0);

  return (
    <div className="card">
      <h3 className="card-title">
        {title}
        <HelpIcon title={title}>{help}</HelpIcon>
      </h3>
      <p className="card-sub">{total} total record{total === 1 ? "" : "s"}.</p>
      {total === 0 ? (
        <p className="muted" style={{ fontSize: 13 }}>No data to chart.</p>
      ) : (
        <svg viewBox={`0 0 ${W} ${H}`} role="img" aria-label={title} style={{ width: "100%", height: "auto", display: "block" }}>
          {/* Y-axis grid + labels */}
          {yTicks.map((t) => {
            const y = PADDING.top + innerH - (t / niceMax) * innerH;
            return (
              <g key={`y-${t}`}>
                <line x1={PADDING.left} x2={W - PADDING.right} y1={y} y2={y} stroke="var(--border)" />
                <text x={PADDING.left - 6} y={y + 3} fontSize={10} textAnchor="end" fill="var(--muted)">{t}</text>
              </g>
            );
          })}
          {/* Bars */}
          {buckets.map((b, i) => {
            const v = values[i];
            const h = (v / niceMax) * innerH;
            const x = PADDING.left + i * stepW + (stepW - barW) / 2;
            const y = PADDING.top + innerH - h;
            return (
              <g key={b}>
                <rect x={x} y={y} width={barW} height={h} fill={BUCKET_BAR_COLORS[b] ?? "#64748b"} rx={3}>
                  <title>{`${labelOf(b)}: ${v}`}</title>
                </rect>
                {v > 0 && (
                  <text x={x + barW / 2} y={y - 4} fontSize={10} textAnchor="middle" fill="var(--text)" fontWeight={600}>{v}</text>
                )}
                <text
                  x={x + barW / 2}
                  y={H - PADDING.bottom + 14}
                  fontSize={10}
                  textAnchor="middle"
                  fill="var(--muted)"
                  style={{ textTransform: "capitalize" }}
                >{labelOf(b)}</text>
              </g>
            );
          })}
          {/* X-axis label */}
          <text x={PADDING.left + innerW / 2} y={H - 4} fontSize={10} fill="var(--muted)" textAnchor="middle">
            Bucket
          </text>
        </svg>
      )}
    </div>
  );
}

function labelOf(b: string): string {
  return b.replace(/_/g, " ");
}

function Stat({ label, value, accent }: { label: string; value: number; accent?: "success" | "warning" | "danger" }) {
  const color = accent === "success" ? "var(--success)" : accent === "warning" ? "var(--warning)" : accent === "danger" ? "var(--danger)" : "var(--text)";
  return (
    <div className="stat">
      <div className="stat-value" style={{ color }}>{value.toLocaleString()}</div>
      <div className="stat-label">{label}</div>
    </div>
  );
}

function Breakdown({ title, data, help }: { title: string; data: Record<string, number>; help?: string }) {
  const entries = Object.entries(data).sort((a, b) => b[1] - a[1]);
  const max = entries[0]?.[1] ?? 1;
  return (
    <div className="breakdown">
      <h3>
        {title}
        {help && <HelpIcon title={title}>{help}</HelpIcon>}
      </h3>
      {entries.length === 0 && <p className="muted" style={{ fontSize: 13 }}>No data.</p>}
      <ul>
        {entries.map(([key, value]) => (
          <li key={key}>
            <span className="bk-label" title={key}>{key.replace(/_/g, " ")}</span>
            <span className="bk-bar"><span style={{ width: `${(value / max) * 100}%` }} /></span>
            <span className="bk-value">{value}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

// --- LINE CHART (axes + labels, no library) ------------------------------

function LineChart({ points, yLabel = "Cases" }: { points: CaseTrendPoint[]; yLabel?: string }) {
  if (points.length === 0) return null;

  const PADDING = { top: 16, right: 16, bottom: 30, left: 36 };
  const W = 720;
  const H = 240;
  const innerW = W - PADDING.left - PADDING.right;
  const innerH = H - PADDING.top - PADDING.bottom;

  const max = Math.max(...points.map((p) => p.count), 1);
  const yTicks = niceTicks(max, 4);          // [0, 5, 10, 15] etc.
  const niceMax = yTicks[yTicks.length - 1];

  const xStep = points.length > 1 ? innerW / (points.length - 1) : innerW;

  // Compute the x-axis label sampling — too many → overlap. Show ~6 evenly.
  const xLabelEvery = Math.max(1, Math.ceil(points.length / 6));

  const path = points
    .map((p, i) => {
      const x = PADDING.left + i * xStep;
      const y = PADDING.top + innerH - (p.count / niceMax) * innerH;
      return `${i === 0 ? "M" : "L"} ${x} ${y}`;
    })
    .join(" ");

  const areaPath =
    `M ${PADDING.left} ${PADDING.top + innerH} ` +
    points
      .map((p, i) => `L ${PADDING.left + i * xStep} ${PADDING.top + innerH - (p.count / niceMax) * innerH}`)
      .join(" ") +
    ` L ${PADDING.left + (points.length - 1) * xStep} ${PADDING.top + innerH} Z`;

  return (
    <svg viewBox={`0 0 ${W} ${H}`} role="img" aria-label="Cases per day" style={{ width: "100%", height: "auto", display: "block" }}>
      {/* Y-axis grid + labels */}
      {yTicks.map((t) => {
        const y = PADDING.top + innerH - (t / niceMax) * innerH;
        return (
          <g key={`y-${t}`}>
            <line x1={PADDING.left} x2={W - PADDING.right} y1={y} y2={y} stroke="var(--border)" />
            <text x={PADDING.left - 6} y={y + 3} fontSize={10} textAnchor="end" fill="var(--muted)">
              {t}
            </text>
          </g>
        );
      })}

      {/* Y-axis label */}
      <text
        transform={`rotate(-90, 12, ${PADDING.top + innerH / 2})`}
        x={12}
        y={PADDING.top + innerH / 2}
        fontSize={10}
        fill="var(--muted)"
        textAnchor="middle"
      >
        {yLabel}
      </text>

      {/* Area + line */}
      <path d={areaPath} fill="var(--accent-soft)" />
      <path d={path} fill="none" stroke="var(--accent)" strokeWidth={2} />

      {/* Data dots + tooltips */}
      {points.map((p, i) => {
        const x = PADDING.left + i * xStep;
        const y = PADDING.top + innerH - (p.count / niceMax) * innerH;
        return (
          <g key={p.date}>
            <circle cx={x} cy={y} r={3} fill="#fff" stroke="var(--accent)" strokeWidth={1.5} />
            <title>{`${p.date}: ${p.count} case${p.count === 1 ? "" : "s"}`}</title>
          </g>
        );
      })}

      {/* X-axis labels */}
      {points.map((p, i) => {
        if (i % xLabelEvery !== 0 && i !== points.length - 1) return null;
        const x = PADDING.left + i * xStep;
        return (
          <text
            key={`x-${p.date}`}
            x={x}
            y={H - PADDING.bottom + 14}
            fontSize={10}
            textAnchor="middle"
            fill="var(--muted)"
          >
            {fmtShortDate(p.date)}
          </text>
        );
      })}

      {/* X-axis label */}
      <text x={PADDING.left + innerW / 2} y={H - 4} fontSize={10} fill="var(--muted)" textAnchor="middle">
        Date
      </text>
    </svg>
  );
}

function niceTicks(max: number, count: number): number[] {
  const step = niceStep(max / count);
  const ticks: number[] = [];
  for (let v = 0; v <= max + step / 2; v += step) ticks.push(Math.round(v * 100) / 100);
  return ticks;
}
function niceStep(rough: number): number {
  if (rough <= 0) return 1;
  const exp = Math.pow(10, Math.floor(Math.log10(rough)));
  const norm = rough / exp;
  const nice = norm < 1.5 ? 1 : norm < 3 ? 2 : norm < 7 ? 5 : 10;
  return nice * exp;
}
function fmtShortDate(iso: string): string {
  // YYYY-MM-DD → "May 8"
  const d = new Date(iso + "T00:00:00Z");
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric", timeZone: "UTC" });
}

// --- RAW DATA TAB ----------------------------------------------------------

function RawDataTab() {
  const cases = useQuery({ queryKey: ["report.rawCases"], queryFn: () => CasesApi.list() });

  if (cases.isLoading) return <LoadingPlaceholder />;
  if (cases.error) return <ErrorBox label="Cases" err={cases.error} />;
  const rows = cases.data?.items ?? [];
  if (rows.length === 0) return <div className="empty">No cases visible. Create one or check your scope.</div>;

  return (
    <div className="card" style={{ padding: 0, overflow: "hidden" }}>
      <table className="audit-table">
        <thead>
          <tr>
            <th>Title</th>
            <th>Status</th>
            <th>Classification</th>
            <th>Branch</th>
            <th>Subject</th>
            <th>Created</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((c: Case) => (
            <tr key={c.id}>
              <td>{c.title}</td>
              <td><span className={`badge ${c.status === "closed" ? "success" : c.status === "open" ? "warning" : ""}`}>{c.status.replace("_", " ")}</span></td>
              <td><ClassificationBadge level={c.classification} /></td>
              <td><code>{c.branchId}</code></td>
              <td>{c.subjectName || "—"}</td>
              <td><code>{c.createdAt ? new Date(c.createdAt).toLocaleString() : "—"}</code></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ClassificationBadge({ level }: { level: Case["classification"] }) {
  const cls = level === "secret" ? "danger" : level === "restricted" ? "warning" : "success";
  return <span className={`badge ${cls}`}>{level}</span>;
}

// --- shared bits ----------------------------------------------------------

function LoadingPlaceholder() {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <div className="skeleton" style={{ height: 70 }} />
      <div className="skeleton" style={{ height: 220 }} />
    </div>
  );
}

function ErrorBox({ label, err }: { label: string; err: unknown }) {
  const msg = err instanceof ApiError
    ? `HTTP ${err.status}: ${err.message}`
    : err instanceof Error
      ? err.message
      : String(err);
  return (
    <div className="error">
      <strong>{label} failed.</strong> {msg}
    </div>
  );
}

// Suppress unused-import warning when api is referenced via subimport
void api;
